import { getNamedRangeFromWorksheet, getNamedRangesByPrefixFromWorksheet } from "../../workbook";
import { quantityPlusUOMnumberFormat } from "../../styles/worksheet";
import type { RowFormat } from "./print";

/** A cost-per column: where it is on the sheet and the quantity its cells divide by. */
export interface CostDenominatorColumn {
  /** Absolute worksheet column index. */
  columnIndex: number;
  value: number | string;
  uom: string;
}

interface WriteCostDenominatorsArgs {
  worksheet: Excel.Worksheet;
  columns: CostDenominatorColumn[];
  costDenominatorsRowIndex: number;
  /** First data row of the report, styled as a cost-per cell unless `applyStyleToFirstRow` is off. */
  firstRowIndex: number;
  firstMarkupRowIndex?: number;
  subTotalsRangeNamePrefix?: string;
  grandTotalRangeName?: string;
  applyStyleToFirstRow: boolean;
  /** SOV sheets use the plain cost-per styles; the Estimate report uses the Detail ones. */
  isSOV: boolean;
}

/**
 * Writes each cost-per column's denominator into the cost denominators row and styles the column's
 * data, markup, subtotal and grand-total cells. The row formulas divide by the cell written here.
 */
export const writeCostDenominators = async ({
  worksheet,
  columns,
  costDenominatorsRowIndex,
  firstRowIndex,
  firstMarkupRowIndex,
  subTotalsRangeNamePrefix,
  grandTotalRangeName,
  applyStyleToFirstRow,
  isSOV,
}: WriteCostDenominatorsArgs): Promise<void> => {
  if (columns.length === 0) return;

  const subTotalsRanges = subTotalsRangeNamePrefix
    ? await getNamedRangesByPrefixFromWorksheet(worksheet, subTotalsRangeNamePrefix)
    : [];
  const grandTotalRange = grandTotalRangeName ? await getNamedRangeFromWorksheet(worksheet, grandTotalRangeName) : null;

  const subTotalsRowIndices: number[] = [];
  for (const range of subTotalsRanges) {
    try {
      subTotalsRowIndices.push(range.rowIndex);
    } catch {
      // skip if not available
    }
  }
  grandTotalRange?.load("rowIndex");
  await worksheet.context.sync();
  const grandTotalRowIndex = grandTotalRange?.rowIndex;

  for (const { columnIndex, value, uom } of columns) {
    if (applyStyleToFirstRow) {
      worksheet.getCell(firstRowIndex, columnIndex).style = "CostPerColumn";
    }

    const denomCell = worksheet.getCell(costDenominatorsRowIndex, columnIndex);
    denomCell.style = "Denominators";
    denomCell.values = [[value]];
    denomCell.numberFormat = [[quantityPlusUOMnumberFormat(uom)]];

    if (firstMarkupRowIndex !== undefined) {
      worksheet.getCell(firstMarkupRowIndex, columnIndex).style = isSOV ? "CostPerColumn" : "DetailCostPerColumn";
    }
    for (const subTotalsRowIndex of subTotalsRowIndices) {
      worksheet.getCell(subTotalsRowIndex, columnIndex).style = isSOV ? "CostPerSubtotal" : "DetailCostPerSubtotal";
    }
    if (grandTotalRowIndex !== undefined) {
      worksheet.getCell(grandTotalRowIndex, columnIndex).style = isSOV
        ? "CostPerGrandtotal"
        : "DetailCostPerGrandtotal";
    }
  }
  await worksheet.context.sync();
};

/** The estimate reports' cost-per style for a printed row, matching the row's own format. */
const costPerRowStyle = (format: RowFormat): string => {
  switch (format.style) {
    case "subtotal":
      return `PerSubtotal${Math.min(format.level + 1, 4)}`;
    case "header":
      return `PerHeader${Math.min(format.level + 1, 4)}`;
    case "sovHeader":
      return `PerSOVHeader${Math.min(format.level + 1, 4)}`;
    case "sectionHeader":
      return "PerSectionHeader";
    default:
      return "DetailCostPerColumn";
  }
};

/**
 * Styles the cost-per cells of every printed row of an estimate report to match the row's format.
 * The SOV styles its rows as it fills each block; the estimate reports mix item, header and subtotal
 * rows, so their cost-per cells are styled once the rows are printed.
 * @param columnIndices absolute worksheet column indices of the cost-per columns
 */
export const styleCostPerRows = async (
  worksheet: Excel.Worksheet,
  dataRowName: string,
  formatList: RowFormat[],
  columnIndices: number[]
): Promise<void> => {
  if (columnIndices.length === 0) return;
  const dataRange = await getNamedRangeFromWorksheet(worksheet, dataRowName);
  if (!dataRange) return;
  dataRange.load("rowIndex");
  await worksheet.context.sync();

  formatList.forEach((format, rowOffset) => {
    const style = costPerRowStyle(format);
    for (const columnIndex of columnIndices) {
      worksheet.getCell(dataRange.rowIndex + rowOffset, columnIndex).style = style;
    }
  });
  await worksheet.context.sync();
};
